/**
 * Deep Research Backfill · Data Ops (Feb 2026 v15).
 *
 * Kicks off bulk Perplexity Sonar Pro on every pharma company that is
 * either missing `franchise_anchors` or has a `bd_intel_updated_at`
 * older than 30 days. Populates franchise_anchors + company_archetype
 * + therapeutic_areas_specific etc. so the v13/v14/v15 rationale
 * guards can actually fire against real data.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, PlayCircle, RefreshCw, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DeepResearchBackfillPage() {
  const [s, setS] = useState({ status: 'idle' });
  const [starting, setStarting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/data-ops/deep-research-backfill/status`, { withCredentials: true });
      setS(data || { status: 'idle' });
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 5000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const start = async () => {
    setStarting(true);
    try {
      await axios.post(`${API}/api/data-ops/deep-research-backfill/start`, {}, { withCredentials: true });
      toast.success('Deep Research backfill started. This runs in the background — check back in ~15-30 min.');
      fetchStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to start.'); }
    finally { setStarting(false); }
  };

  const cancel = async () => {
    if (!window.confirm('Force-cancel this backfill? Pharmas already enriched keep their new data.')) return;
    try {
      await axios.post(`${API}/api/data-ops/deep-research-backfill/reset`, {}, { withCredentials: true });
      toast.success('Cancelled.');
      fetchStatus();
    } catch (e) { toast.error('Reset failed'); }
  };

  const total = s.total || 0;
  const processed = s.processed || 0;
  const pct = total > 0 ? Math.round((processed * 100) / total) : 0;
  const running = s.status === 'running';
  const stalled = s.status === 'stalled';
  const completed = s.status === 'completed';

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Deep Research · Bulk Backfill
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600 leading-relaxed">
            Runs Perplexity Sonar Pro on every pharma missing <code>franchise_anchors</code> or with data older than 30 days.
            Populates franchise_anchors (per-TA marquee products), company_archetype (innovator / specialty_pharma / medical_devices / clinical_stage_biotech / …), therapeutic_areas_specific, preferred_modalities, excluded_modalities, scientific_preferences.
            This is the data the v13/v14/v15 rationale guards need to fire correctly.
          </p>

          <div className="flex items-center gap-2">
            {running && <Badge className="bg-sky-100 text-sky-700">RUNNING</Badge>}
            {completed && <Badge className="bg-emerald-100 text-emerald-700">COMPLETED</Badge>}
            {stalled && <Badge className="bg-amber-100 text-amber-800">STALLED · worker died</Badge>}
            {s.status === 'cancelled' && <Badge className="bg-slate-200 text-slate-700">CANCELLED</Badge>}
            {(!s.status || s.status === 'idle') && <Badge variant="outline">IDLE</Badge>}
          </div>

          {stalled && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50/60 p-3">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-800 leading-relaxed">
                <p className="font-semibold">Worker died mid-run — likely after a redeploy.</p>
                <p className="mt-1">{processed}/{total} pharmas enriched before the crash. Click Cancel → Start Backfill to resume on the rest.</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-4 gap-3 text-center">
            <div className="rounded-md border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Processed</div>
              <div className="text-2xl font-bold">{processed}</div>
              <div className="text-[10px] text-slate-400">of {total} ({pct}%)</div>
            </div>
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700">Enriched</div>
              <div className="text-2xl font-bold text-emerald-700">{s.ok || 0}</div>
            </div>
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3">
              <div className="text-[10px] uppercase tracking-wider text-rose-700">Errors</div>
              <div className="text-2xl font-bold text-rose-700">{s.errors || 0}</div>
            </div>
            <div className="rounded-md border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Skipped</div>
              <div className="text-2xl font-bold">{s.skipped || 0}</div>
            </div>
          </div>

          {running && (
            <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-sky-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={start} disabled={starting || running} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="start-backfill-btn">
              {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</> : <><PlayCircle className="w-4 h-4 mr-2" /> Start Backfill</>}
            </Button>
            <Button onClick={fetchStatus} variant="outline" size="sm">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
            {(stalled || running) && (
              <Button onClick={cancel} variant="outline" className="text-xs border-rose-300 text-rose-600 hover:bg-rose-50" data-testid="cancel-backfill-btn">
                Cancel job
              </Button>
            )}
          </div>

          <div className="text-[11px] text-slate-500 border-t pt-3 mt-4">
            <p><strong>Runtime estimate:</strong> ~2 pharmas/sec sustained (Perplexity rate limit). ~2,000 pharmas ≈ 17 minutes. Cost ≈ $0.03/pharma × 2,000 ≈ $60 in API calls.</p>
            <p className="mt-1">Safe to leave the page — job runs in the background. Heartbeat writes every 5 pharmas; crash recovery flips to <em>stalled</em> if heartbeat is >5 min old.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
